import React, { useState } from 'react';
import { Modal, ScrollView, TouchableOpacity, View } from 'react-native';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useToast } from 'react-native-toast-notifications';
import dayjs from 'dayjs';
import 'dayjs/locale/es';

import tw from '../../../../styles/tailwind';
import Text from '../../../../components/Text';
import SignatureView from '../../../../components/SignatureView';
import api from '../../../../utils/api';
import IconButton from '../../../../components/IconButton';
import Icon from '@/components/Icon';
import GuardAuthorizationEntryModal, { OutsideHoursBanner } from './GuardAuthorizationEntryModal';
import { isOutsideServiceHours, useAccessAuthorizationConfig } from './accessAuthorizationConfig';

dayjs.locale('es');


/**
 * Detalle de una responsiva en caseta, con el registro de entrada o salida.
 * `source`: qr si se abrió desde el escáner, search si se eligió de la lista.
 */
export default function GuardAccessAuthorizationDetailModal({ visible, authorization, communityId, source = 'search', onRequestClose }: any) {
  const insets = useSafeAreaInsets();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { serviceHours } = useAccessAuthorizationConfig(communityId);
  const [registeringEntry, setRegisteringEntry] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const [exitError, setExitError] = useState<string | null>(null);

  // La firma, is_inside y los ingresos solo vienen en el detalle (show), no en el listado
  const { data: detail, refetch } = useQuery<any>({
    queryKey: ['guard.access-authorization', authorization?.uuid],
    queryFn: () => api.get(`/communities/${communityId}/access-authorizations/${authorization.uuid}`).then(r => r.data),
    enabled: !!visible && !!authorization?.uuid && !!communityId,
  });

  if (!authorization) return null;

  const auth = detail ?? authorization;
  const isSuspended = auth.status === 'suspended';
  const isInside = !!auth.is_inside;
  const insideSince = isInside ? (auth.last_entry_log ?? auth.entry_logs?.find((l: any) => l.entry_time && !l.exit_time))?.entry_time : null;
  // Movimientos de hoy (entry_logs viene del más reciente al más antiguo)
  const todayLogs: any[] = (detail?.entry_logs ?? [])
    .filter((l: any) => dayjs(l.entry_time ?? l.exit_time).isSame(dayjs(), 'day'))
    .reverse();
  const hasEnteredToday = todayLogs.some((l: any) => l.entry_time);

  function onAccessChanged() {
    refetch();
    queryClient.invalidateQueries({ queryKey: ['guard.access-authorizations.today', communityId] });
    queryClient.invalidateQueries({ queryKey: ['guard.access-authorizations.inside', communityId] });
  }

  async function registerExit() {
    setIsExiting(true);
    setExitError(null);
    try {
      await api.post(`/communities/${communityId}/access-authorizations/${authorization.uuid}/exit`);
      toast.show('Salida registrada', { type: 'success' });
      onAccessChanged();
      onRequestClose();
    } catch (err: any) {
      // Dentro del Modal el toast no se ve
      setExitError(err?.response?.data?.message ?? 'No se pudo registrar la salida');
    } finally {
      setIsExiting(false);
    }
  }

  return (
    <Modal
      visible={!!visible}
      onRequestClose={onRequestClose}
      statusBarTranslucent
      animationType='slide'
    >
      <ScrollView style={tw`flex-1 bg-white pt-[${insets.top}px]`} contentContainerStyle={tw`px-5 pb-[${insets.bottom + 40}px]`}>
        <View style={tw`row justify-between items-center mt-5 mb-4`}>
          <Text style={tw`text-primary text-lg font-600`}>Ingreso</Text>
          <TouchableOpacity onPress={onRequestClose}>
            <Icon name='close' size={26} color={tw.color('red')} />
          </TouchableOpacity>
        </View>

        <Row label='Domicilio' value={authorization.unit?.name} />
        <Row label='Responsable' value={authorization.requester?.name} />
        <Row label='Servicio' value={authorization.service_type || authorization.description} />
        {!!authorization.service_type && !!authorization.description && (
          <Row label='Descripción' value={authorization.description} />
        )}

        {authorization.provider_name && (
          <Row label='Proveedor' value={authorization.provider_name} />
        )}

        <Row label='Acceso' value={authorization.access_type_str} />

        <Row
          label='Vigencia'
          value={
            authorization.end_date && authorization.end_date !== authorization.start_date
              ? `${dayjs(authorization.start_date).format('D MMM')} – ${dayjs(authorization.end_date).format('D MMM YYYY')}`
              : dayjs(authorization.start_date).format('ddd D MMM YYYY')
          }
        />

        <Row label='Horario' value={formatTime(authorization)} />

        {authorization.vehicle_info && (
          <View style={tw`mt-4 p-4 bg-gray-50 rounded-xl`}>
            <Text style={tw`text-gray-500 mb-2 font-500`}>Vehículo</Text>
            {authorization.vehicle_info.brand_model && (
              <Text style={tw`text-gray-700`}>{authorization.vehicle_info.brand_model}</Text>
            )}
            {authorization.vehicle_info.plate_number && (
              <Text style={tw`text-gray-700 mt-1 tracking-widest font-600`}>{authorization.vehicle_info.plate_number}</Text>
            )}
            {authorization.vehicle_info.color && (
              <Text style={tw`text-gray-500 mt-1`}>Color: {authorization.vehicle_info.color}</Text>
            )}
          </View>
        )}

        <SignatureView xml={auth.signature} />

        <View style={tw`mt-6`}>
          {isSuspended ? (
            <View style={tw`p-4 bg-red-50 border border-red-200 rounded-xl`}>
              <Text style={tw`text-red-700 font-600 mb-1`}>Autorización suspendida</Text>
              {auth.suspended_reason && <Text style={tw`text-red-700`}>{auth.suspended_reason}</Text>}
              <Text style={tw`text-red-700 mt-2`}>No se puede registrar la entrada.</Text>
            </View>
          ) : (
            <View style={tw`p-4 bg-green-50 border border-green-200 rounded-xl`}>
              <Text style={tw`text-green-700`}>Esta autorización fue avalada por la administración.</Text>
            </View>
          )}

          {!isSuspended && !isInside && isOutsideServiceHours(serviceHours) && (
            <View style={tw`mt-3`}><OutsideHoursBanner /></View>
          )}

          {todayLogs.length > 0 && (
            <View style={tw`mt-4 p-4 bg-gray-50 rounded-xl`}>
              <Text style={tw`text-gray-500 mb-2 font-500`}>Movimientos de hoy</Text>
              {todayLogs.map((l: any) => (
                <Text key={l.uuid} style={tw`text-gray-700 mb-1`}>
                  {l.entry_time ? `Entró ${dayjs(l.entry_time).format('HH:mm')}` : 'Salida sin entrada registrada'}
                  {l.exit_time ? ` · Salió ${dayjs(l.exit_time).format('HH:mm')}` : (l.entry_time ? ' · Sigue adentro' : '')}
                </Text>
              ))}
            </View>
          )}

          {isInside && (
            <Text style={tw`text-gray-600 mt-4 text-center`}>
              Adentro desde las {insideSince ? dayjs(insideSince).format('HH:mm') : '—'}
            </Text>
          )}

          {!!exitError && (
            <View style={tw`p-3 mt-4 bg-red-50 border border-red-200 rounded-xl`}>
              <Text style={tw`text-red-700`}>{exitError}</Text>
            </View>
          )}

          {/* La salida se permite aunque esté suspendida */}
          {isInside ? (
            <IconButton
              title={isExiting ? 'Registrando...' : 'Registrar salida'}
              icon='close'
              color='secondary'
              disabled={isExiting}
              onPress={registerExit}
              style={tw`mt-4 py-3`}
            />
          ) : !isSuspended && (
            <IconButton
              title={hasEnteredToday ? 'Registrar reingreso' : 'Registrar entrada'}
              icon='check'
              onPress={() => setRegisteringEntry(true)}
              style={tw`mt-4 py-3`}
            />
          )}
        </View>
      </ScrollView>

      <GuardAuthorizationEntryModal
        visible={registeringEntry}
        authorization={auth}
        communityId={communityId}
        source={source}
        onRequestClose={() => setRegisteringEntry(false)}
        onRegistered={() => {
          setRegisteringEntry(false);
          onAccessChanged();
          onRequestClose();
        }}
      />
    </Modal>
  );
}


function Row({ label, value }: any) {
  return (
    <View style={tw`mb-4`}>
      <Text style={tw`text-gray-500 mb-1`}>{label}</Text>
      <Text style={tw`text-gray-800 text-lg font-500`}>{value}</Text>
    </View>
  );
}


function formatTime(auth: any) {
  if (!auth.start_time) return 'Todo el día';
  const start = auth.start_time.slice(0, 5);
  const end = auth.end_time ? auth.end_time.slice(0, 5) : null;
  return end ? `De ${start} a ${end}` : `Desde ${start}`;
}
